import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useAuthContext } from '@/providers/auth-provider';
import { StreamProvider } from '@/providers/stream-provider';
import { fallback } from '@/components/fallback';
import { PrivateRoute } from './private-route';
import { paths } from '@/config/paths';
import { PageTracker, AuthTracker } from '@/analytics';
import { AppLayout } from '@/components/app-layout/app-layout';
import { TitleManager } from './title-manager';
import { getSubdomainContext, AGENT_ID_TO_LOGIN_SLUG } from '@/config/subdomain-config';
import { UserRole } from '@/types/user-role';
import { useLoadAgents } from '@/hooks/use-agents';

// Lazy load views for code splitting
const LoginView = lazy(() => import('@/views/login-view/login-view').then(module => ({ default: module.LoginView })));
const AgentLoginView = lazy(() => import('@/views/login-view/agent-login-view').then(module => ({ default: module.AgentLoginView })));
const CallbackView = lazy(() => import('@/views/callback-view/callback-view').then(module => ({ default: module.CallbackView })));
const AssistantView = lazy(() => import('@/views/assistant-view/assistant-view').then(module => ({ default: module.AssistantView })));
const AgentSelectorView = lazy(() => import('@/views/agent-selector-view/agent-selector-view').then(module => ({ default: module.AgentSelectorView })));
const TestCanvasView = lazy(() => import('@/views/test-canvas-view/test-canvas-view').then(module => ({ default: module.TestCanvasView })));
const TestEventOrchestrationView = lazy(() => import('@/views/test-event-orchestration-view/test-event-orchestration-view').then(module => ({ default: module.TestEventOrchestrationView })));
const TestIncidentWorkflowView = lazy(() => import('@/views/test-incident-workflow-view/test-incident-workflow-view').then(module => ({ default: module.TestIncidentWorkflowView })));
const ScrollTestView = lazy(() => import('@/views/scroll-test-view/scroll-test-view').then(module => ({ default: module.ScrollTestView })));
const ColorPaletteTest = lazy(() => import('@/components/color-palette-test').then(module => ({ default: module.ColorPaletteTest })));

const UserSettingsView = lazy(() => import('@/views/user-settings-view/user-settings-view').then(module => ({ default: module.UserSettingsView })));
const AccessDeniedView = lazy(() => import('@/views/access-denied-view/access-denied-view').then(module => ({ default: module.AccessDeniedView })));
// Migration assistant landing page — standalone, lives outside AppLayout
// because it has its own full-bleed hero + floating CTA.
const MigrationOverviewView = lazy(() => import('@/views/migration-overview/migration-overview-view'));

// Best Practices views
const BestPracticesOverviewView = lazy(() => import('@/views/best-practices-overview-view/best-practices-overview-view').then(module => ({ default: module.BestPracticesOverviewView })));
const BestPracticesUsersView = lazy(() => import('@/views/best-practices-users-view/best-practices-users-view').then(module => ({ default: module.BestPracticesUsersView })));
const BestPracticesTeamsView = lazy(() => import('@/views/best-practices-teams-view/best-practices-teams-view').then(module => ({ default: module.BestPracticesTeamsView })));
const BestPracticesContactMethodsView = lazy(() => import('@/views/best-practices-contact-methods-view/best-practices-contact-methods-view').then(module => ({ default: module.BestPracticesContactMethodsView })));
const BestPracticesSchedulesView = lazy(() => import('@/views/best-practices-schedules-view/best-practices-schedules-view').then(module => ({ default: module.BestPracticesSchedulesView })));
const BestPracticesFlexibleSchedulesView = lazy(() => import('@/views/best-practices-flexible-schedules-view/best-practices-flexible-schedules-view').then(module => ({ default: module.BestPracticesFlexibleSchedulesView })));
const BestPracticesNotificationRulesView = lazy(() => import('@/views/best-practices-notification-rules-view/best-practices-notification-rules-view').then(module => ({ default: module.BestPracticesNotificationRulesView })));
const BestPracticesEscalationPoliciesView = lazy(() => import('@/views/best-practices-escalation-policies-view/best-practices-escalation-policies-view').then(module => ({ default: module.BestPracticesEscalationPoliciesView })));
const BestPracticesServicesView = lazy(() => import('@/views/best-practices-services-view/best-practices-services-view').then(module => ({ default: module.BestPracticesServicesView })));
const BestPracticesAIOpsView = lazy(() => import('@/views/best-practices-aiops-view/best-practices-aiops-view').then(module => ({ default: module.BestPracticesAIOpsView })));
const BestPracticesIncidentWorkflowsView = lazy(() => import('@/views/best-practices-incident-workflows-view/best-practices-incident-workflows-view').then(module => ({ default: module.BestPracticesIncidentWorkflowsView })));
const BestPracticesOptimizationChecksView = lazy(() => import('@/views/best-practices-optimization-checks-view/best-practices-optimization-checks-view').then(module => ({ default: module.BestPracticesOptimizationChecksView })));
const RuleSummaryView = lazy(() => import('@/views/rule-summary-view/rule-summary-view').then(module => ({ default: module.RuleSummaryView })));
const AgentsLandingView = lazy(() =>
  import('@/views/agents-landing-view/agents-landing-view').then(module => ({
    default: module.AgentsLandingView,
  }))
);

// Conditional layout wrapper - suppresses AppLayout for all /login* paths
const ConditionalLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isPublicFullPage =
    location.pathname.startsWith('/login') || location.pathname === '/';

  if (isPublicFullPage) {
    return <>{children}</>;
  }

  return <AppLayout>{children}</AppLayout>;
};

// Resolved once at module load so all renders in the session see the same context.
const subdomainContext = getSubdomainContext();

// On a dedicated agent subdomain this is the only valid login slug.
// null means any slug is allowed (selector or local context).
const lockedLoginSlug =
  subdomainContext.type === 'agent'
    ? (AGENT_ID_TO_LOGIN_SLUG[subdomainContext.agentId] ?? null)
    : null;

// Guards /login/:agentSlug — redirects to the locked slug on agent subdomains.
const AgentLoginRoute: React.FC = () => {
  const { agentSlug } = useParams<{ agentSlug: string }>();
  if (lockedLoginSlug && agentSlug !== lockedLoginSlug) {
    return <Navigate to={`${paths.Login}/${lockedLoginSlug}`} replace />;
  }
  return <AgentLoginView />;
};

export const AppRouter: React.FC = () => {
  const { isAuthenticated, initializeAuthIsLoading, user } = useAuthContext();
  const FallbackComponent = fallback;

  // Boot the agent catalogue once. The hook is idempotent: it bails if the
  // store already has the list or a fetch is in-flight. Mounting here means
  // the catalogue is available before any AssistantView / AgentSelectorView
  // renders, regardless of which route the user lands on.
  useLoadAgents();

  if (initializeAuthIsLoading) {
    return <FallbackComponent message="Initializing application..." />;
  }

  // On an agent subdomain the root and selector path both go directly to that agent.
  const agentRootRedirect =
    subdomainContext.type === 'agent'
      ? `/assistant/${subdomainContext.agentId}`
      : paths.Assistant;

  // TODO: re-enable role restriction once access control is confirmed working
  // const isConsultant = user?.role === UserRole.CONSULTANT;
  // const selectorElement =
  //   subdomainContext.type === 'selector' && !isConsultant ? (
  //     <AccessDeniedView />
  //   ) : (
  //     <AgentSelectorView />
  //   );
  const selectorElement = <AgentSelectorView />;

  return (
    <StreamProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <PageTracker />
        <AuthTracker />
        <TitleManager />
        <ConditionalLayout>
          <Suspense fallback={<FallbackComponent message="Loading content..." />}>
            <Routes>
              {/* Public Routes */}
              <Route
                path={paths.Login}
                element={
                  lockedLoginSlug ? (
                    <Navigate to={`${paths.Login}/${lockedLoginSlug}`} replace />
                  ) : (
                    <LoginView />
                  )
                }
              />
              <Route path={paths.LoginAgent} element={<AgentLoginRoute />} />
              <Route path={paths.Callback} element={<CallbackView />} />

              {/* Protected Routes */}
              <Route
                path={paths.Assistant}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    {subdomainContext.type === 'agent' ? (
                      <Navigate to={`/assistant/${subdomainContext.agentId}`} replace />
                    ) : (
                      selectorElement
                    )}
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.AssistantAgent}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <AssistantView />
                  </PrivateRoute>
                }
              />

              {/* Migration Assistant — standalone overview landing page.
                  Reached from the right-sidebar "Migration overview"
                  button. Authenticated-only; renders its own layout. */}
              <Route
                path={paths.MigrationOverview}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <MigrationOverviewView />
                  </PrivateRoute>
                }
              />

              {/* Test Canvas Route - Public for easy testing */}
              <Route path={paths.TestCanvas} element={<TestCanvasView />} />

              {/* Test Event Orchestration Route - Public for testing Phase 5 */}
              <Route path={paths.TestEventOrchestration} element={<TestEventOrchestrationView />} />

              {/* Test Incident Workflow Route - Public for testing Incident Workflow Preview UI */}
              <Route path={paths.TestIncidentWorkflow} element={<TestIncidentWorkflowView />} />

              {/* Test Scroll Route - Public for testing vertical scroll */}
              <Route path={paths.TestScroll} element={<ScrollTestView />} />

              {/* Color Palette Test Route - Public for viewing custom colors */}
              <Route path={paths.ColorPalette} element={<ColorPaletteTest />} />

              {/* Best Practices Routes */}
              <Route
                path={paths.BestPracticesOverview}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesOverviewView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesUsers}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesUsersView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesTeams}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesTeamsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesContactMethods}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesContactMethodsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesSchedules}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesSchedulesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesFlexibleSchedules}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesFlexibleSchedulesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesNotificationRules}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesNotificationRulesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesEscalationPolicies}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesEscalationPoliciesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesServices}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesServicesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesAIOps}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesAIOpsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesIW}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesIncidentWorkflowsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesOptimizationChecks}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesOptimizationChecksView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.RuleSummary}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <RuleSummaryView />
                  </PrivateRoute>
                }
              />

              {/* Settings Route */}
              <Route
                path={paths.Settings}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <UserSettingsView />
                  </PrivateRoute>
                }
              />

              {/* Redirect /best-practices to /best-practices/overview */}
              <Route
                path={paths.BestPractices}
                element={<Navigate to={paths.BestPracticesOverview} replace />}
              />

              {/* Root redirect */}
              <Route
                path={paths.Root}
                element={
                  subdomainContext.type === 'agent' ? (
                    <Navigate to={agentRootRedirect} replace />
                  ) : (
                    <AgentsLandingView />
                  )
                }
              />

              {/* Fallback to login for any unknown routes */}
              <Route path="*" element={<Navigate to={paths.Login} replace />} />
            </Routes>
          </Suspense>
        </ConditionalLayout>
      </Router>
    </StreamProvider>
  );
};

export default AppRouter;
